import type { UpsellItem } from '@bulknutrients/bulk-shared-lib/interfaces/ICart';
import { AddToCartStatus } from '@components/Button/AddToCart';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { CustomImage } from '@components/Media/Image/Image';
import { SkeletonImage } from '@components/Placeholders/Skeleton';
import { useAddItems } from '@hooks/cart/useAddItems';
import { log } from '@lib/logs';
import { useContext, useEffect, useState } from 'react';
import { ToastAlert } from './QuickCart/QuickCartUpsell';

const bannerHeight = 134;

export function CartUpsell({
  upsellItem,
  showUpsellBanner,
}: {
  upsellItem: UpsellItem | undefined;
  showUpsellBanner: boolean;
}) {
  const { addingToCartStatus, setAddingToCartStatus } =
    useContext<UIContextInterface>(UIContext);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Add upsell item to cart
  const addItem = useAddItems({
    include: ['line_items.physical_items.options'],
  });

  const addToCart = async () => {
    setAddingToCartStatus(AddToCartStatus.Adding);
    try {
      if (!upsellItem) throw new Error('Upsell item is undefined');
      const { error } = await addItem({
        lineItems: [
          {
            productId: upsellItem.variant.productId,
            variantId: upsellItem.variant.variantId,
            quantity: 1,
          },
        ],
      });

      if (error) throw Error(error);

      setAddingToCartStatus(AddToCartStatus.Added);
      // show confirmation message after adding upsell item to cart
      setShowConfirmation(true);
    } catch (err) {
      setAddingToCartStatus(AddToCartStatus.Failed);
      log({ error: err, location: 'CartUpsell addToCart' });
    }
  };

  //hide success message when upsell banner appears
  useEffect(() => {
    if (showUpsellBanner) {
      setShowConfirmation(false);
    }
  }, [showUpsellBanner]);

  // return empty div when upsell item does not exist
  if (!upsellItem || (!showUpsellBanner && !showConfirmation)) return <></>;

  return (
    <div className="relative flex min-h-[70px] items-center justify-center">
      <ToastAlert
        open={showConfirmation}
        onOpenChange={setShowConfirmation}
        message={upsellItem?.message}
      />

      {addingToCartStatus !== AddToCartStatus.Adding && (
        <button
          type="button"
          data-product-id={upsellItem.variant.productId}
          onClick={addToCart}
          className={`${
            showUpsellBanner && !showConfirmation ? 'block' : 'hidden'
          } cart-upsell-item mx-0 my-2.5 w-full translate-x-0 border-none bg-transparent p-0`}
        >
          <CustomImage
            image={upsellItem.image}
            width={691}
            height={bannerHeight}
          />
        </button>
      )}

      {addingToCartStatus === AddToCartStatus.Adding && (
        <SkeletonImage aspectRatio="aspect-[691/134]" widthPixels={691} />
      )}
    </div>
  );
}
